// Pure, storage/DOM-adjacent helpers behind the Light/Dark/System theme
// selector (see src/context/ThemeContext.tsx for the React side). Kept
// separate and framework-free so the persistence/resolution logic is easy
// to unit test, the same split used by src/utils/languagePreference.ts for
// the other cross-cutting "pick a site-wide preference" feature.
//
// public/index.html runs an inline, synchronous copy of getStoredTheme +
// resolveTheme + applyResolvedTheme before the React bundle loads, so the
// very first paint already has the right data-theme attribute (no
// light-then-dark flash). Keep that inline script's logic in sync with this
// file if either changes.

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "nihongotech.theme";

const VALID_THEMES: readonly Theme[] = ["light", "dark", "system"];

export function getStoredTheme(): Theme | null {
    try {
        const value = localStorage.getItem(THEME_STORAGE_KEY);
        return VALID_THEMES.includes(value as Theme) ? (value as Theme) : null;
    } catch {
        return null;
    }
}

export function saveTheme(theme: Theme): void {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // No persistence available (private browsing, storage disabled) -
        // the theme still applies for this page load via React state.
    }
}

// matchMedia is unavailable in some test/prerendering environments (jsdom
// included, unless a test installs it - see useMediaQuery.ts for the same
// guard) - default to "light" rather than throwing.
export function systemPrefersDark(): boolean {
    return typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveTheme(theme: Theme, prefersDark: boolean = systemPrefersDark()): ResolvedTheme {
    if (theme === "system") return prefersDark ? "dark" : "light";
    return theme;
}

// The single DOM write every theme change (and the anti-flash inline
// script) funnels through, so there is exactly one place that decides how
// a resolved theme is expressed in markup.
export function applyResolvedTheme(resolved: ResolvedTheme): void {
    document.documentElement.setAttribute("data-theme", resolved);
}
